import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, ChevronUp, X, Plus, ArrowLeft } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import { Card, CardContent } from '../../components/ui/card';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/ui/BackNavigationButton';
import NavigationButton from '../../components/ui/NavigationButton';
import Select from '../../components/forms/scheduleForm/Select';
import SelectItem from '../../components/forms/scheduleForm/SelectItem';
import SelectTrigger from '../../components/forms/scheduleForm/SelectTrigger';
import SelectContent from '../../components/forms/scheduleForm/SelectContent';
import SelectValue from '../../components/forms/scheduleForm/SelectValue';
import Tooltip from '../../components/ui/ToolTip';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { exampleShiftRestrictions } from '../../components/utils/exampleUtils';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';
import useAutoSave from '../../hooks/useAutoSave';

const EmployeeShiftRestrictionsPage = () => {
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [restrictions, setRestrictions] = useState({});
  const [expandedEmployees, setExpandedEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  
  const nextButtonRef = useRef(null);
  const restrictionsContainerRef = useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const isExampleMode = location.state?.isExample;

  useAutoSave('shiftRestrictions', restrictions)

  const tutorialSteps = [
    {
      title: t("tutorial.shiftRestrictions.title") || "Shift Restrictions",
      description: t("tutorial.shiftRestrictions.description") || "Here you can set specific shift restrictions for each employee.",
      position: "center"
    },
    {
      title: t("tutorial.shiftRestrictions.employees.title") || "Employee Selection",
      description: t("tutorial.shiftRestrictions.employees.description") || "Click on an employee to expand their settings.",
      position: "top-right",
      elementRef: restrictionsContainerRef
    },
    {
      title: t("tutorial.shiftRestrictions.next.title") || "Ready to Continue",
      description: t("tutorial.shiftRestrictions.next.description") || "Click Next to continue to Staff Requirements.",
      position: "next-button",
      elementRef: nextButtonRef
    }
  ];

  useEffect(() => {
    const loadData = () => {
      try {
        if (isExampleMode) {
          setShowTutorial(true);
          
          const savedEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
          const savedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
          
          setEmployees(savedEmployees);
          setSchedules(savedSchedules);
          setRestrictions(exampleShiftRestrictions);
          
          if (Object.keys(exampleShiftRestrictions).length > 0) {
            const employeesToExpand = Object.keys(exampleShiftRestrictions)
              .map(employeeId =>
                savedEmployees.findIndex(emp => emp.number.toString() === employeeId)
              )
              .filter(index => index !== -1);
          
            setExpandedEmployees(employeesToExpand);
          }
          
          sessionStorage.setItem('shiftRestrictions', JSON.stringify(exampleShiftRestrictions));
        } else {
          const savedEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
          const savedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
          const savedRestrictions = JSON.parse(sessionStorage.getItem('shiftRestrictions') || '{}');
          
          setEmployees(savedEmployees);
          setSchedules(savedSchedules);
          setRestrictions(savedRestrictions);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        showAlert({
          title: t('alert.error'),
          description: t('alert.loadDataError'),
          variant: 'destructive'
        });
        setIsLoading(false);
      }
    };

    loadData();
  }, [showAlert, t, isExampleMode]);

  const saveRestrictions = () => {
    try {
      if (!isExampleMode) {
        sessionStorage.setItem('shiftRestrictions', JSON.stringify(restrictions));
        const savedRestrictions = JSON.parse(sessionStorage.getItem('shiftRestrictions') || '{}');
        console.log('Restrictions saved:', savedRestrictions);
      }
      return true;
    } catch (error) {
      console.error('Error saving restrictions:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.saveError'),
        variant: 'destructive'
      });
      return false;
    }
  };

  const handleEmployeeExpand = (index) => {
    if (!isExampleMode) {
      setExpandedEmployees(prev =>
        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
      );
    }
  };

  const handleAddRestriction = (employeeId) => {
    if (isExampleMode) return;
    
    const employeeRestrictions = restrictions[employeeId] || [];
    
    setRestrictions({
      ...restrictions,
      [employeeId]: [
        ...employeeRestrictions,
        {
          id: Date.now(),
          scheduleCode: schedules.length > 0 ? schedules[0].code : '',
          allowOnly: true
        }
      ]
    });
  };

  const handleRemoveRestriction = (employeeId, restrictionId) => {
    if (isExampleMode) return;
    
    const employeeRestrictions = restrictions[employeeId] || [];
    
    setRestrictions({
      ...restrictions,
      [employeeId]: employeeRestrictions.filter(r => r.id !== restrictionId)
    });
  };

  const handleRestrictionChange = (employeeId, restrictionId, field, value) => {
    if (isExampleMode) return;
    
    const employeeRestrictions = restrictions[employeeId] || [];
    const restrictionIndex = employeeRestrictions.findIndex(r => r.id === restrictionId);
    
    if (restrictionIndex !== -1) {
      const updatedRestrictions = [...employeeRestrictions];
      updatedRestrictions[restrictionIndex] = {
        ...updatedRestrictions[restrictionIndex],
        [field]: value
      };
      
      setRestrictions({
        ...restrictions,
        [employeeId]: updatedRestrictions
      });
    }
  };

  const getScheduleName = (code) => {
    const schedule = schedules.find(s => s.code === code);
    return schedule ? `${schedule.code} (${schedule.legend})` : code;
  };

  const getEmployeeRestrictions = (employeeId) => {
    return restrictions[employeeId] || [];
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const handleBack = () => {
    if (!isExampleMode) {
      saveRestrictions();
    }
    
    navigate('/form/absences', {
      state: { isExample: isExampleMode }
    });
  };

  const handleNext = () => {
    if (!isExampleMode) {
      saveRestrictions();
    }
    
    navigate('/form/transitions', {
      state: { isExample: isExampleMode }
    });
  };

  const decimalToTimeString = (decimalTime) => {
    if (decimalTime === 0) return '00:00';
    if (decimalTime === 24) return '24:00';
    
    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);
    
    if (minutes === 60) {
      return `${hours + 1}:00`;
    }
    
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatShiftCode = (shiftCode) => {
    if (!shiftCode) return '';
    
    let formatted = shiftCode.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)[.,](\d+)-(\d+)/g, 
      (match, prefix, prefixHours, prefixMins, startHours, startMins, endHours) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const startTime = decimalToTimeString(parseFloat(`${startHours}.${startMins}`));
        return `(${prefix}${prefixTime}) ${startTime}-${endHours}:00`;
      }
    );
    
    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)-(\d+)[.,](\d+)/g, 
      (match, prefix, prefixHours, prefixMins, startHours, endHours, endMins) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const endTime = decimalToTimeString(parseFloat(`${endHours}.${endMins}`));
        return `(${prefix}${prefixTime}) ${startHours}-${endTime}`;
      }
    );
    
    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)/g, 
      (match, prefix, hours, mins) => {
        return `(${prefix}${decimalToTimeString(parseFloat(`${hours}.${mins}`))})`;
      }
    );
    
    formatted = formatted.replace(/(\d+)-(\d+)[.,](\d+)/g, 
      (match, start, hours, mins) => {
        return `${start}-${decimalToTimeString(parseFloat(`${hours}.${mins}`))}`;
      }
    );
    
    formatted = formatted.replace(/(\d+)[.,](\d+)/g, 
      (match, hours, mins) => {
        return decimalToTimeString(parseFloat(`${hours}.${mins}`));
      }
    );
    
    return formatted;
  };

  return (
    <div className='-mt-16'>
      <Breadcrumbs pages={pages}/>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-6xl mx-auto space-y-8">
        <ScenarioIndicator />

          <div className="flex justify-between items-center">
            <BackButton onClick={handleBack} />
            <div className="flex items-center gap-2 mb-6">
              <h1 className="text-2xl font-bold">
                {t("shiftRestrictions.title") || "Employee Shift Restrictions"}
              </h1>
              <div className="flex items-center h-8">
                <Tooltip content={t("shiftRestrictions.tooltip") || "Define which shifts employees can or cannot work"}/>
              </div>
            </div>
            <NavigationButton ref={nextButtonRef} onClick={handleNext} />
          </div>


          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : employees.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-amber-500 mb-4">
                  <Calendar className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium">{t("shiftRestrictions.noEmployees") || "No employees found"}</h3>
                <p className="text-gray-500 mt-2 mb-4">
                  {t("shiftRestrictions.selectTeamFirst") || "Please select a team first"}
                </p>
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <ArrowLeft className="inline-block mr-2 h-4 w-4" />
                  {t("shiftRestrictions.goBack") || "Go Back"}
                </button>
              </CardContent>
            </Card>
          ) : schedules.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-amber-500 mb-4">
                  <Calendar className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium">{t("shiftRestrictions.noSchedules") || "No schedules defined"}</h3>
                <p className="text-gray-500 mt-2 mb-4">
                  {t("shiftRestrictions.defineSchedulesFirst") || "Please define schedules first"}
                </p>
                <button
                  onClick={() => navigate('/form/schedule', { state: { isExample: isExampleMode } })}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <ArrowLeft className="inline-block mr-2 h-4 w-4" />
                  {t("shiftRestrictions.goToSchedules") || "Define Schedules"}
                </button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" ref={restrictionsContainerRef}>
              {employees.map((employee, employeeIndex) => {
                const employeeId = employee.number.toString();
                const employeeRestrictions = getEmployeeRestrictions(employeeId);
                
                return (
                  <Card 
                    key={employeeId} 
                    className={`border ${expandedEmployees.includes(employeeIndex) ? 'border-indigo-300' : 'border-gray-200'}`}
                  >
                    <div 
                      className="p-4 flex justify-between items-center cursor-pointer"
                      onClick={() => handleEmployeeExpand(employeeIndex)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="font-semibold text-indigo-600">{employee.name.substring(0, 2)}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{employee.name}</h3>
                          <p className="text-sm text-gray-500">
                            {employeeRestrictions.length === 0 
                              ? (t("shiftRestrictions.noRestrictionsSet") || "No shift restrictions set") 
                              : (t("shiftRestrictions.restrictionsCount", { count: employeeRestrictions.length }) || 
                                 `${employeeRestrictions.length} restriction(s) set`)}
                          </p>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        {expandedEmployees.includes(employeeIndex) ? <ChevronUp /> : <ChevronDown />}
                      </div>
                    </div>

                    {expandedEmployees.includes(employeeIndex) && (
                      <CardContent className="border-t border-gray-200 p-4">
                        <div className="space-y-4">
                          {employeeRestrictions.map((restriction) => (
                            <div key={restriction.id} className="bg-gray-50 p-4 rounded-lg relative">
                              <button 
                                className={`absolute top-2 right-2 text-gray-400 hover:text-red-500 ${isExampleMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={() => handleRemoveRestriction(employeeId, restriction.id)}
                                disabled={isExampleMode}
                              >
                                <X className="h-5 w-5" />
                              </button>
                              
                              <div className="grid md:grid-cols-2 gap-4 mt-2">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t("shiftRestrictions.shift") || "Shift"}
                                  </label>
                                  <Select
                                    value={restriction.scheduleCode}
                                    onValueChange={(value) => handleRestrictionChange(employeeId, restriction.id, 'scheduleCode', value)}
                                    disabled={isExampleMode}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue>
                                        {restriction.scheduleCode ? getScheduleName(restriction.scheduleCode) : (t("shiftRestrictions.selectShift") || "Select a shift")}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {schedules.map((schedule) => (
                                        <SelectItem key={schedule.code} value={schedule.code}>
                                          {formatShiftCode(schedule.code)} ({schedule.legend})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t("shiftRestrictions.restrictionType") || "Restriction Type"}
                                  </label>
                                  <Select
                                    value={restriction.allowOnly ? "allow" : "prevent"}
                                    onValueChange={(value) => handleRestrictionChange(employeeId, restriction.id, 'allowOnly', value === "allow")}
                                    disabled={isExampleMode}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue>
                                        {restriction.allowOnly 
                                          ? (t("shiftRestrictions.allowOnly") || "Allow only this shift") 
                                          : (t("shiftRestrictions.preventThis") || "Prevent this shift")}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="allow">
                                        {t("shiftRestrictions.allowOnly") || "Allow only this shift"}
                                      </SelectItem>
                                      <SelectItem value="prevent">
                                        {t("shiftRestrictions.preventThis") || "Prevent this shift"}
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <button
                            onClick={() => handleAddRestriction(employeeId)}
                            disabled={isExampleMode}
                            className={`w-full py-2 flex items-center justify-center gap-2 rounded-lg transition-colors ${
                              isExampleMode 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200'
                            }`}
                          >
                            <Plus className="h-5 w-5" />
                            {t("shiftRestrictions.addRestriction") || "Add Shift Restriction"}
                          </button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
        <div className="h-32"></div>
      </div>
      
      <TutorialOverlay 
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
};

export default EmployeeShiftRestrictionsPage;